import React, { useState } from 'react';
import {
  usePermissions,
} from 'react-admin';
import {
  Card,
  CardContent,
  CircularProgress,
  CardHeader,
  Grid,
  Divider,
} from '@mui/material';

import { reportNames, SelectableReportTypes } from '../../constants/reportTypes';
import actions, { Actions } from '../../constants/actions';
import { ReportCategories } from '../../constants/reportCategories';
import { reportCategoriesDefinition } from './features/reportCategoryFeatures';
import { getMatchingPermissions } from '../../utilities/hasAccess';
import { attributesSchema } from '../../utilities/reports/permissions';

import ReportCategorySelector from './ReportCategorySelector';
import ReportCreate from './ReportCreate';
import ReportHistoryTable from './ReportHistoryTable';
import useAllowedReportTypesChoices from './useAllowedReportTypesChoices';

const ReportList = () => {
  const { permissions } = usePermissions<Actions[]>();
  const matchingPermissions = getMatchingPermissions(
    permissions,
    actions.REPORT_CREATE,
    attributesSchema,
  );
  const allowedReportTypes = useAllowedReportTypesChoices(matchingPermissions);
  const { allowedReportTypesChoices, isLoading } = allowedReportTypes;

  const [selectedCategoryId, setSelectedCategoryId] = useState<ReportCategories | null>(null);
  const [selectedReportType, setSelectedReportType] = useState<SelectableReportTypes | null>(null);

  const allowedTypesSet = new Set(allowedReportTypesChoices.map(({ id }) => id));

  const visibleCategories = reportCategoriesDefinition.filter(
    (category) => category.reportTypes.some((type) => allowedTypesSet.has(type)),
  );

  const selectedCategory = visibleCategories.find(({ id }) => id === selectedCategoryId) ?? null;
  const allowedCategoryTypes = selectedCategory?.reportTypes
    ?.filter((type) => allowedTypesSet.has(type)) ?? [];

  const handleCategoryClick = (categoryId: ReportCategories) => {
    if (selectedCategoryId === categoryId) {
      setSelectedCategoryId(null);
      setSelectedReportType(null);
    } else {
      setSelectedCategoryId(categoryId);
      setSelectedReportType(null);
    }
  };

  const handleReportTypeClick = (rt: SelectableReportTypes) => {
    setSelectedReportType(selectedReportType === rt ? null : rt);
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <Grid container spacing={4}>
      <Grid size={12}>
        <ReportCategorySelector
          visibleCategories={visibleCategories}
          categoryReportTypeIds={allowedCategoryTypes}
          selectedCategoryId={selectedCategoryId}
          selectedReportType={selectedReportType}
          onCategoryClick={handleCategoryClick}
          onReportTypeClick={handleReportTypeClick}
        />
      </Grid>
      <Grid size={12}>
        {selectedReportType && (
          <Card variant="outlined">
            <CardHeader title={`Report configuration: ${reportNames[selectedReportType]}`} />
            <Divider />
            <CardContent>
              <ReportCreate reportType={selectedReportType} />
            </CardContent>
          </Card>
        )}
      </Grid>
      <Grid size={12}>
        <ReportHistoryTable
          selectedCategoryId={selectedCategoryId}
          selectedReportType={selectedReportType}
          allowedCategoryTypes={allowedCategoryTypes}
        />
      </Grid>
    </Grid>
  );
};

export default ReportList;
